import type { FC, ReactNode } from 'react'
import type { LayoutProps } from '@/types'

import { useRef } from 'react'

import { cn } from '@/lib/utils'
import { gsap } from '@/lib/gsap'
import { useIsomorphicLayoutEffect } from '@/hooks/use-isomorphic-layout-effect'

interface ParallaxProps extends LayoutProps {
    className?: string
    /** Vertical travel in pixels across the full viewport pass. */
    speed?: number
    /** Horizontal travel in pixels across the full viewport pass. */
    x?: number
    rotate?: number
}

/**
 * Scroll-linked drift. Several of these at different speeds inside one
 * section is what gives the galleries their depth: nothing moves at the
 * same rate, so the composition keeps rearranging as you pass it.
 */
export const Parallax: FC<ParallaxProps> = ({
    children,
    className,
    speed = 80,
    x = 0,
    rotate = 0
}): ReactNode => {
    const root = useRef<HTMLDivElement>(null)

    useIsomorphicLayoutEffect(() => {
        const element = root.current

        if (!element) {
            return
        }

        const context = gsap.context(() => {
            if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
                return
            }

            gsap.fromTo(
                element,
                { y: speed / 2, x: x / 2, rotate: rotate / 2 },
                {
                    y: -speed / 2,
                    x: -x / 2,
                    rotate: -rotate / 2,
                    ease: 'none',
                    scrollTrigger: {
                        trigger: element,
                        start: 'top bottom',
                        end: 'bottom top',
                        scrub: 1
                    }
                }
            )
        }, element)

        return () => context.revert()
    }, [speed, x, rotate])

    return (
        <div ref={root} className={cn('will-change-transform', className)}>
            {children}
        </div>
    )
}
